import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, MonitorSmartphone, ShieldCheck, Check } from 'lucide-react';
import { api } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import {
  Acao,
  Cartao,
  ModalEmail,
  ModalSenha,
  Modal2fa,
  Rotulo,
} from '../paginas/Perfil.js';

/**
 * Perfil do cliente no portal.
 *
 * Uma versão enxuta do perfil interno: o cliente cuida da própria conta —
 * nome, e-mail, senha, verificação em duas etapas e sessões — e escolhe se quer
 * o resumo diário de alterações. As notificações de operação do cluster (sem
 * quorum, provisionamento, etc.) não aparecem aqui: são da equipe interna, não
 * da operadora. Reaproveita os cartões e modais do perfil interno para as duas
 * telas mudarem num lugar só.
 */

interface PerfilDados {
  nome: string;
  email: string;
  tfaAtivo: boolean;
  operadora: { id: string; nome: string } | null;
  sessoesAtivas: number;
  preferencias: { resumoDiario: boolean };
}

export function PerfilPortal() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<'senha' | 'email' | '2fa' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['perfil'],
    queryFn: () => api.get<PerfilDados>('/perfil'),
  });

  const salvarNome = useMutation({
    mutationFn: (nome: string) => api.patch('/perfil', { nome }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['perfil'] }),
  });

  const salvarPrefs = useMutation({
    mutationFn: (preferencias: { resumoDiario: boolean }) => api.patch('/perfil', { preferencias }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['perfil'] }),
  });

  const encerrarSessoes = useMutation({
    mutationFn: () => api.post<{ encerradas: number }>('/perfil/encerrar-outras-sessoes'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['perfil'] }),
  });

  const [nome, setNome] = useState('');
  useEffect(() => {
    if (data) setNome(data.nome);
  }, [data]);

  if (isLoading || !data) return <Esqueleto />;

  return (
    <div className="grid max-w-[900px] gap-5 lg:grid-cols-[1fr_300px]">
      <div className="space-y-5">
        <Cartao titulo="Seus dados">
          <label className="block">
            <Rotulo>Nome</Rotulo>
            <div className="flex gap-2">
              <input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="h-11 flex-1 rounded-controle border border-borda px-3 text-[14px] focus:border-marca focus:outline-none"
              />
              <Botao
                variante="primario"
                onClick={() => salvarNome.mutate(nome)}
                carregando={salvarNome.isPending}
                disabled={nome.trim() === data.nome || nome.trim().length < 2}
              >
                Salvar
              </Botao>
            </div>
          </label>

          <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0">
              <Rotulo>E-mail</Rotulo>
              <p className="truncate font-mono text-[13.5px]">{data.email}</p>
            </div>
            <Botao tamanho="tabela" onClick={() => setModal('email')}>
              Trocar e-mail
            </Botao>
          </div>
        </Cartao>

        <Cartao
          titulo="Notificações"
          subtitulo="Escolha se quer acompanhar as mudanças por e-mail."
        >
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={data.preferencias.resumoDiario}
              onChange={(e) => salvarPrefs.mutate({ resumoDiario: e.target.checked })}
              className="mt-0.5 size-4 shrink-0 accent-marca"
            />
            <span className="min-w-0 cursor-pointer">
              <span className="block text-[13.5px] font-bold">Resumo diário</span>
              <span className="block text-[12.5px] leading-snug text-texto-suave">
                Um e-mail por dia com as alterações dos seus blocos e domínios — reversos
                trocados, registros e solicitações revisadas.
              </span>
            </span>
          </label>
        </Cartao>
      </div>

      <div className="space-y-5">
        <Cartao titulo="Conta">
          <div className="space-y-2.5 text-[13px]">
            <div className="flex justify-between gap-2">
              <span className="text-texto-suave">Operadora</span>
              <span className="font-bold">{data.operadora?.nome ?? '—'}</span>
            </div>
          </div>
        </Cartao>

        <Cartao titulo="Segurança">
          <div className="space-y-3">
            <Acao
              Icone={KeyRound}
              titulo="Senha"
              detalhe="Trocar exige a senha atual e encerra as outras sessões."
              botao="Trocar"
              aoClicar={() => setModal('senha')}
            />
            <Acao
              Icone={ShieldCheck}
              titulo="Verificação em duas etapas"
              detalhe={
                data.tfaAtivo
                  ? 'Ativa. Um código do aplicativo é pedido a cada login.'
                  : 'Desativada. Recomendada para proteger o acesso.'
              }
              botao={data.tfaAtivo ? 'Desativar' : 'Ativar'}
              aoClicar={() => setModal('2fa')}
              destaque={!data.tfaAtivo}
            />
            <Acao
              Icone={MonitorSmartphone}
              titulo={`${data.sessoesAtivas} ${data.sessoesAtivas === 1 ? 'sessão ativa' : 'sessões ativas'}`}
              detalhe="Encerra o acesso em todos os outros aparelhos e navegadores."
              botao="Encerrar outras"
              aoClicar={() => encerrarSessoes.mutate()}
              carregando={encerrarSessoes.isPending}
              desabilitado={data.sessoesAtivas <= 1}
            />
          </div>

          {encerrarSessoes.data && (
            <p className="mt-3 flex items-center gap-1.5 text-[12.5px] font-semibold text-sucesso">
              <Check size={14} /> {encerrarSessoes.data.encerradas} sessões encerradas.
            </p>
          )}
        </Cartao>
      </div>

      {modal === 'senha' && <ModalSenha aoFechar={() => setModal(null)} />}
      {modal === 'email' && <ModalEmail aoFechar={() => setModal(null)} />}
      {modal === '2fa' && <Modal2fa ativo={data.tfaAtivo} aoFechar={() => setModal(null)} />}
    </div>
  );
}

function Esqueleto() {
  return (
    <div className="grid max-w-[900px] gap-5 lg:grid-cols-[1fr_300px]">
      <div className="space-y-5">
        <div className="h-40 animate-pulse rounded-card border border-borda bg-superficie" />
        <div className="h-32 animate-pulse rounded-card border border-borda bg-superficie" />
      </div>
      <div className="space-y-5">
        <div className="h-24 animate-pulse rounded-card border border-borda bg-superficie" />
        <div className="h-64 animate-pulse rounded-card border border-borda bg-superficie" />
      </div>
    </div>
  );
}
